import type { UniBoxConfig } from './types';
import {
  FUNNEL_ORDER,
  SALES_OUTCOMES,
  SALES_SUBSTATUS_BY_STAGE,
  type SubstatusDef,
} from './taxonomy';

/**
 * Sales Intelligence config.
 *
 * - `statuses` is the module's slice of the GLOBAL lead-status enum (taxonomy.ts).
 *   Sales uses the full 6-stage funnel.
 * - `substatusByStage` is this module's OWN internal substatus vocabulary —
 *   keyed by stage, not global. Other modules declare their own.
 * - `outcomes` is the module's outcome vocabulary (separate DB column).
 * - `funnelColumns` is the Kanban column order FOR THIS MODULE. Each AI Worker
 *   type defines its own status sequence here, so Kanban-as-home renders a
 *   different board per module without code changes.
 */
export const salesConfig: UniBoxConfig = {
  product: 'sales_intelligence',
  entity_label: 'Lead',
  group_by: { key: 'campaign_id', label: 'Campaign' },
  // The global enum, in funnel order. Kanban columns come from funnelColumns.
  statuses: FUNNEL_ORDER,
  substatusByStage: SALES_SUBSTATUS_BY_STAGE,
  outcomes: SALES_OUTCOMES,
  funnelColumns: FUNNEL_ORDER,
  channels: ['whatsapp', 'email', 'sms', 'call', 'linkedin', 'instagram', 'x'],
  automation_modes: ['autopilot', 'copilot', 'manual'],
  default_automation: 'autopilot',
  view_modes: ['inbox', 'calendar', 'kanban'],
  queue_label: 'Outreach',
  sequence_labels: {
    1: 'Icebreaker',
    2: 'Follow-up',
    3: 'Last message',
  },
};

/**
 * AI Workers (Orders) config — a DIFFERENT status sequence. This module's
 * funnel is order-fulfilment, not lead-development, so it has its own statuses,
 * substatuses and Kanban columns. Demonstrates the config-driven, per-module
 * model: same components, different board.
 */
export const ordersConfig: UniBoxConfig = {
  product: 'ai_workers_orders',
  entity_label: 'Customer',
  group_by: { key: 'channel_account', label: 'Account' },
  statuses: ['new', 'processing', 'fulfilled', 'issue', 'closed'],
  substatusByStage: {
    new: [{ key: 'order_placed', label: 'Order placed' }, { key: 'payment_pending', label: 'Payment pending' }] as SubstatusDef[],
    processing: [{ key: 'confirmed', label: 'Confirmed' }, { key: 'shipped', label: 'Shipped' }] as SubstatusDef[],
    fulfilled: [{ key: 'delivered', label: 'Delivered' }] as SubstatusDef[],
    issue: [{ key: 'refund', label: 'Refund' }, { key: 'complaint', label: 'Complaint' }] as SubstatusDef[],
    closed: [],
  },
  outcomes: [
    { key: 'waiting', label: 'Waiting' },
    { key: 'resolved', label: 'Resolved' },
    { key: 'escalated', label: 'Escalated' },
  ],
  funnelColumns: ['new', 'processing', 'fulfilled', 'issue', 'closed'],
  channels: ['whatsapp', 'email', 'instagram', 'x', 'messenger', 'telegram'],
  automation_modes: ['autopilot', 'copilot', 'manual'],
  default_automation: 'autopilot',
  view_modes: ['inbox', 'kanban'],
  queue_label: 'Order Queue',
  sequence_labels: {},
};
